/**
 * Render contract v0 - NEW SIBLING FILE (approved contract extension,
 * 2026-10-21). The original frozen contracts (sim.ts, entities.ts, social.ts)
 * stay byte-identical; this file adds the render-facing shapes only.
 * Wire hashing/encoding lives in src/sim/core/snapshot-sections.ts.
 */

/** Fixed-point 16.16, serialized as i64 (see src/sim/core/fixed.ts). */
export type Fx = number;

export type Gait = 0 | 1 | 2 | 3; // 0 idle, 1 graze, 2 walk, 3 run

export interface RenderPose {
  id: string;          // animal id (entities.ts); serialized as u32 FNV-1a hash
  speciesIdx: number;  // u16 index into the species table, ordered by sorted species id (data/species/*.json ids sorted ascending); Mgmt-Sim's collectRenderPoses emits this order, Render-World must use the same table.
  x: Fx;               // east-west, 16.16 fixed-point millimeters
  z: Fx;               // north-south (renderer maps z -> world z)
  heading: number;     // u16: 0..65535 maps 0..2*PI clockwise from north
  gait: Gait;
  speed: number;       // u32, mm per tick (magnitude; direction from heading)
  scale: number;       // u16, 8.8 fixed-point multiplier (1.0 = 256; juveniles, heroes)
}

export interface ViewState {
  mode: 'reserve' | 'field';
  focusAnimalId?: string; // set when the camera tracks one animal
}

/**
 * Binary layout of SimSnapshot.entities (canonical, little-endian):
 *
 *   Header:  4 bytes magic 'EAE1' | u32 sectionCount
 *   Section: u32 fourcc | u32 byteLength | payload (padded to 4)
 *
 *   fourcc 'POSE' (RenderPose array):
 *     u32 count
 *     per pose (32-byte stride):
 *       u32  idHash (FNV-1a 32 of id - see snapshot-sections.ts)
 *       u16  speciesIdx
 *       u8   gait
 *       u8   _pad0
 *       i64  x (16.16 fixed mm)
 *       i64  z (16.16 fixed mm)
 *       u16  heading
 *       u16  scale (8.8 fixed-point, 1.0 = 256)
 *       u32  speed (mm/tick)
 *
 *   fourcc 'FILD' (FieldSlice): layout in contracts/field.ts.
 *
 *   fourcc 'UIST' (additive, 2026-10-21): canonical payload-encoded
 *   (writePayload) object of UI-facing state contributed by systems via
 *   collectUiState - { [systemName]: state }. UI decodes; never owns state.
 *
 * Sections are always written in ascending fourcc order. A section with no
 * content is still present with count 0 so readers get a stable frame format.
 */
export const SNAPSHOT_ENTITIES_MAGIC = 'EAE1';
export const SECTION_POSE = 0x504f5345; // 'POSE'
export const SECTION_FILD = 0x46494c44; // 'FILD'
export const SECTION_UIST = 0x55495354; // 'UIST'
